import { Flex, VStack, AccordionIcon, Text, useBreakpointValue } from "@chakra-ui/react";
import Tooltip from "components/Tooltip";
import { LengthOfHtmlTextString, truncateHtml } from "helpers/stringHelper";
import SingleAccordion from "newui/components/SingleAccordion";
import SkeletonBase from "newui/components/Skeleton";
import TextBoxHTML from "newui/components/TextBoxHTML";
import { ReactNode, useCallback, useMemo, useState } from "react";

interface AccordionLargeTextDisplayProps {
	isLoading: boolean;
	maxTextDisplay?: number;
	text: string | null;
	title: string | null;
}

export const AccordionLargeTextDisplay: React.FC<
	AccordionLargeTextDisplayProps
> = ({ isLoading, maxTextDisplay = 150, text, title }) => {
	const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
	const [hideAccordionButton, setHideAccordionButton] = useState(false);
	const [mobileExpanded, setMobileExpanded] = useState(false);
	const toggleMobile = useCallback(() => setMobileExpanded((prev) => !prev), []);

	const length = LengthOfHtmlTextString(text);

	const hasText = length > 0;

	const truncatedHtml = useMemo(() => {
		if (!text) {
			return null;
		}
		return truncateHtml(text, { maxCharacters: maxTextDisplay, maxLines: 3 });
	}, [text, maxTextDisplay]);

	if (!hasText) {
		return null;
	}

	if (isLoading) {
		return (
			<SkeletonBase
				width="100%"
				height="48px"
			/>
		);
	}

	/* ── Mobile: compact expand/collapse with plain-text preview ── */
	if (isMobile) {
		const plainText = (text ?? "")
			.replace(/<[^>]*>/g, "")
			.replace(/&nbsp;/g, " ")
			.replace(/&amp;/g, "&")
			.replace(/&lt;/g, "<")
			.replace(/&gt;/g, ">")
			.replace(/&quot;/g, '"')
			.replace(/\s+/g, " ")
			.trim();
		const mobileMax = maxTextDisplay > 0 ? maxTextDisplay : 200;
		const isTruncated = plainText.length > mobileMax;
		const previewText = isTruncated ? plainText.slice(0, mobileMax) : plainText;

		// Not truncated — static, non-interactive
		if (!isTruncated) {
			return (
				<div className="border-t border-gray-100 pt-3">
					{title && (
						<h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
							{title}
						</h5>
					)}
					<div className="pt-2">
						<TextBoxHTML html={text ?? ""} className="text-base font-normal text-gray-600" />
					</div>
				</div>
			);
		}

		return (
			<div
				className="border-t border-gray-100 pt-3"
				onClick={!mobileExpanded ? toggleMobile : undefined}
				role={!mobileExpanded ? "button" : undefined}
			>
				{title && (
					<h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
						{title}
					</h5>
				)}
				{mobileExpanded ? (
					<div className="pt-2">
						<TextBoxHTML html={text ?? ""} className="text-base font-normal text-gray-600" />
						<button type="button" onClick={toggleMobile} className="mt-1 text-sm font-medium text-blue-600">
							Click to collapse
						</button>
					</div>
				) : (
					<div className="cursor-pointer pt-2">
						<p className="text-sm text-gray-600">{previewText}</p>
						<span className="mt-1 inline-block text-sm font-medium text-blue-600">
							Click to view more
						</span>
					</div>
				)}
			</div>
		);
	}

	const content = (
		title: string,
		displayValue: string,
		isUsingAccordion = true
	): ReactNode => {
		return (
			<>
				<p className="text-left font-roboto font-bold text-gray-text-400">
					{title}
					{isUsingAccordion && <AccordionIcon />}
				</p>
				<TextBoxHTML
					html={displayValue}
					className="text-gray-text-600 text-base font-normal"
				/>
				{isUsingAccordion && (
					<div className="inline-block rounded-md bg-blue-100 p-1">
						<i>click to see additional text...</i>
					</div>
				)}
			</>
		);
	};

	if (!truncatedHtml.wasTruncated) {
		return <div className="my-2">{content(title, text ?? "", false)}</div>;
	}

	const getButtonContent = (title: string, displayValue: string): ReactNode => {
		return (
			<div className="pl-1">
				<Tooltip
					label="Click to view all"
					placement="top-start"
				>
					<Flex
						w="full"
						justify="flex-start"
					>
						<VStack align={"start"}>{content(title, `${displayValue}`)}</VStack>
					</Flex>
				</Tooltip>
			</div>
		);
	};

	const getPanelContent = (
		title: string,
		displayValue: string,
		close: () => void
	) => {
		return (
			<div className="">
				<Tooltip
					label="Click to hide"
					placement="top-start"
				>
					<p
						className="text-gray-text-600 cursor-pointer p-1 pb-2 font-roboto font-bold hover:bg-[#dbeafe]"
						onClick={close}
					>
						{title} <AccordionIcon />
					</p>
				</Tooltip>
				<TextBoxHTML
					html={displayValue}
					className="text-base font-normal text-gray-600"
				/>
			</div>
		);
	};

	const renderAccordionButton = (isOpen: boolean): ReactNode => {
		if (isOpen) {
			return null;
		}

		return getButtonContent(title, truncatedHtml?.value);
	};

	return (
		hasText && (
			<SingleAccordion
				buttonProps={{ style: { textAlign: "left" } }}
				hideButton={hideAccordionButton}
				onAccordionChange={setHideAccordionButton}
				renderButtonContent={renderAccordionButton}
				renderPanelContent={(close, open) =>
					getPanelContent(title, text, close)
				}
			/>
		)
	);
};
